import { Link, Outlet, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import Logo from '@/components/Logo';

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/como-funciona', label: 'Cómo Funciona' },
    { path: '/precios', label: 'Precios' },
    { path: '/blog', label: 'Blog' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Logo linkTo="/" variant="dark" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-blue-500'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                Iniciar sesión
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
              >
                Prueba gratis
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 text-sm font-medium ${
                    location.pathname === link.path
                      ? 'text-blue-500'
                      : 'text-slate-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-slate-600"
                >
                  Iniciar sesión
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full py-2.5 bg-blue-500 text-white text-sm font-medium rounded-lg text-center"
                >
                  Prueba gratis
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 sm:col-span-2 md:col-span-1">
              <div className="mb-4">
                <Logo linkTo="/" variant="light" />
              </div>
              <p className="text-slate-400 text-sm max-w-md">
                Software de gestión de costes para restaurantes. Controla tus escandallos,
                optimiza tu food cost y maximiza la rentabilidad de tu negocio.
              </p>
              <p className="text-slate-400 text-sm mt-4">
                Contacto:{' '}
                <a href="mailto:hello@pleko.app" className="hover:text-white">hello@pleko.app</a>
              </p>
              <div className="flex space-x-4 mt-4">
                <a href="https://www.linkedin.com/company/pleko-app/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href="https://www.instagram.com/plekoapp" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
              </div>
            </div>

            {/* Producto */}
            <div>
              <h4 className="font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/como-funciona" className="hover:text-white">Cómo funciona</Link></li>
                <li><Link to="/precios" className="hover:text-white">Precios</Link></li>
                <li><Link to="/software-gestion-restaurante" className="hover:text-white">Software gestión restaurante</Link></li>
                <li><Link to="/carta-digital-restaurante" className="hover:text-white">Carta digital restaurante</Link></li>
                <li><Link to="/control-horario-hosteleria" className="hover:text-white">Control horario hostelería</Link></li>
                <li><Link to="/register" className="hover:text-white">Prueba gratis</Link></li>
              </ul>
            </div>

            {/* Recursos / SEO landings */}
            <div>
              <h4 className="font-semibold mb-4">Recursos</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/blog" className="hover:text-white font-medium text-slate-300">Blog</Link></li>
                <li><Link to="/escandallo" className="hover:text-white">Qué es un escandallo</Link></li>
                <li><Link to="/food-cost" className="hover:text-white">Calcular food cost</Link></li>
                <li><Link to="/mermas-cocina" className="hover:text-white">Mermas en cocina</Link></li>
                <li><Link to="/calculadora-food-cost" className="hover:text-white">Calculadora food cost</Link></li>
                <li><Link to="/plantilla-escandallo-excel" className="hover:text-white">Plantilla escandallo Excel</Link></li>
                <li><Link to="/tabla-mermas-alimentos" className="hover:text-white">Tabla de mermas</Link></li>
                <li><Link to="/ficha-tecnica-cocina" className="hover:text-white">Ficha técnica de cocina</Link></li>
                <li><Link to="/calcular-coste-plato" className="hover:text-white">Calcular coste de plato</Link></li>
                <li><Link to="/appcc-restaurante" className="hover:text-white">APPCC restaurante</Link></li>
                <li><Link to="/calendario-temporada" className="hover:text-white">Calendario de temporada</Link></li>
                <li><Link to="/precios-mercado" className="hover:text-white">Precios mercado mayorista</Link></li>
                <li><Link to="/indice-precios-mayoristas" className="hover:text-white font-medium text-slate-300">Índice PLEKO de Precios</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/privacidad" className="hover:text-white">Privacidad</Link></li>
                <li><Link to="/terminos" className="hover:text-white">Términos</Link></li>
                <li><Link to="/cookies" className="hover:text-white">Cookies</Link></li>
                <li><Link to="/reembolsos" className="hover:text-white">Reembolsos</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            <p suppressHydrationWarning>&copy; {new Date().getFullYear()} PLEKO. Todos los derechos reservados.</p>
            <p className="mt-2">
              Desarrollado por{' '}
              <a
                href="https://theharbor.digital"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
              >
                theharbor.digital
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
